import { useEffect, useRef, useState } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  useDisclosure,
  NativeSelect,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'
import { useUnionSearch } from '../hooks/useUnion'
import { useAuth } from '../hooks/useAuth'
import { useClientConfig } from '../hooks/useClientConfig'
import { UnionFormModal } from '../components/union/UnionFormModal'
import { ResponsiveTable, FilterBar, type ResponsiveColumn } from '../components/shared'
import type { UnionSearchParams, UnionSummary } from '../types/union'

// DT-PROD-2: land the union module on the committee's home union (when one is
// configured) instead of always showing the full listing. This component is
// only mounted at the bare listing route (/dashboard/unions) -- the detail
// route (/dashboard/unions/:id) is a separate page -- so redirecting away from
// here to a union's detail route cannot loop back into this effect.
export function UnionListPage() {
  const navigate = useNavigate()
  const { isOrganizer } = useAuth()
  const createModal = useDisclosure()
  const [params, setParams] = useState<UnionSearchParams>({ page: 1, pageSize: 25 })
  const [searchInput, setSearchInput] = useState('')

  const { data: clientConfig, isLoading: isConfigLoading } = useClientConfig()
  const homeUnionId = clientConfig?.homeUnionId
  const hasRedirected = useRef(false)

  useEffect(() => {
    if (hasRedirected.current) return
    if (isConfigLoading) return
    if (!homeUnionId) return
    hasRedirected.current = true
    navigate(`/dashboard/unions/${homeUnionId}`, { replace: true })
  }, [homeUnionId, isConfigLoading, navigate])

  const { data, isLoading } = useUnionSearch(params)

  const handleSearch = () => {
    setParams(prev => ({ ...prev, search: searchInput || undefined, page: 1 }))
  }

  const totalPages = data ? Math.ceil(data.totalCount / data.pageSize) : 0
  const filterCount = params.isActive !== undefined ? 1 : 0

  const columns: ResponsiveColumn<UnionSummary>[] = [
    {
      header: 'Name',
      mobileRole: 'title',
      cell: (union) => (
        <Text fontWeight="medium" fontSize="sm">
          {union.fullName || union.name}
        </Text>
      ),
    },
    {
      header: 'Local #',
      mobileRole: 'meta',
      cell: (union) => <Text fontSize="sm">{union.localNumber || '—'}</Text>,
    },
    {
      header: 'Region',
      mobileRole: 'meta',
      cell: (union) => <Text fontSize="sm">{union.region || '—'}</Text>,
    },
    {
      header: 'Worksites',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (union) => <Text fontSize="sm">{union.affiliatedWorksiteCount}</Text>,
    },
    {
      header: 'Members',
      mobileRole: 'meta',
      isNumeric: true,
      cell: (union) => <Text fontSize="sm">{union.memberCount}</Text>,
    },
    {
      header: 'Status',
      mobileRole: 'badge',
      cell: (union) => (
        <Badge colorPalette={union.isActive ? 'green' : 'gray'} fontSize="xs">
          {union.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ]

  return (
    <Box p={8}>
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" flexWrap="wrap" gap={3}>
          <Box>
            <Text fontSize="2xl" fontWeight="bold">Unions</Text>
            <Text color="gray.500" fontSize="sm">
              Manage union organizations and their business affiliations
            </Text>
          </Box>
          {isOrganizer && (
            <Button colorPalette="brand" size="sm" onClick={createModal.onOpen}><FiPlus />New Union
                          </Button>
          )}
        </HStack>

        {/* Filters */}
        <FilterBar
          search={{
            value: searchInput,
            onChange: setSearchInput,
            placeholder: 'Search unions...',
            onSubmit: handleSearch,
          }}
          filterCount={filterCount}
          actions={<Button size="sm" onClick={handleSearch}>Search</Button>}
        >
          <NativeSelect.Root size="sm">
            <NativeSelect.Field
              maxW={{ base: 'full', md: '160px' }}
              placeholder="All statuses"
              value={params.isActive === undefined ? '' : String(params.isActive)}
              onChange={e => setParams(prev => ({
                ...prev,
                isActive: e.target.value === '' ? undefined : e.target.value === 'true',
                page: 1,
              }))}>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </NativeSelect.Field>
            <NativeSelect.Indicator />
          </NativeSelect.Root>
        </FilterBar>

        {/* Table */}
        <ResponsiveTable
          columns={columns}
          rows={data?.items ?? []}
          keyFn={(union) => union.id}
          onRowClick={(union) => navigate(`/dashboard/unions/${union.id}`)}
          isLoading={isLoading}
          emptyMessage="No unions found."
        />

        {/* Pagination */}
        {!isLoading && data && totalPages > 1 && (
          <HStack justify="space-between" px={4} py={3}>
            <Text fontSize="sm" color="gray.500">
              {data.totalCount} union{data.totalCount !== 1 ? 's' : ''} found
            </Text>
            <HStack gap={2}>
              <Button
                size="xs"
                disabled={data.page <= 1}
                onClick={() => setParams(prev => ({ ...prev, page: prev.page! - 1 }))}
              >
                Previous
              </Button>
              <Text fontSize="sm">
                Page {data.page} of {totalPages}
              </Text>
              <Button
                size="xs"
                disabled={data.page >= totalPages}
                onClick={() => setParams(prev => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
              >
                Next
              </Button>
            </HStack>
          </HStack>
        )}
      </VStack>

      {/* Create modal */}
      {createModal.open && (
        <UnionFormModal isOpen={createModal.open} onClose={createModal.onClose} />
      )}
    </Box>
  );
}
